import {
  CustomMarker,
  CustomMarkerChartType,
  CustomMarkerDefinition,
  CustomMarkerRanges,
  CustomMarkerResultType,
  CUSTOM_MARKER_SECTION_OPTIONS,
} from './CustomMarker';
import { Section } from './Section';

/**
 * Lab panels LabSmarts ships, offered as a pick list on My Markers.
 *
 * A panel is a test a practitioner ORDERS as one item — Celiac Disease
 * Comprehensive, Quest's Lipoprotein Fractionation — whose results LabSmarts
 * has no fields for. (The NMR LipoProfile was one of these until 2026-10-22,
 * when its nine markers became shipped fields; see the note where that panel
 * used to be.) Without this they define each marker by hand, name, units,
 * chart type
 * and both range bounds, once per account, and the mold panel alone is fourteen
 * of those before an import can fill any of them.
 *
 * THE ONE RULE: this catalog is a SEED, and the practitioner's library copy is
 * authoritative. Picking a panel copies its markers into their library exactly
 * as typing them would, so revising a panel in a later release cannot touch a
 * library that already took it, they can edit or delete any row it added, and
 * nothing downstream of the library learns a panel existed. It is the rule the
 * library already applies to a test, one layer up:
 *
 *   catalog  --copy-->  practitioner's library  --copy-->  the test
 *
 * WHERE THE NUMBERS COME FROM, which is the part worth arguing with: panel
 * MEMBERSHIP, units and the Result LOINC are Labcorp's own (the DOS Compendium,
 * November 2024), and every RANGE is the reference interval Labcorp prints
 * beside that row on a real report. The compendium carries no ranges at all, so
 * `scripts/buildLabcorpPanels.mjs` reads both and leaves out any row no report
 * in the corpus gives a usable range for — `validateCustomMarkerRangeStrings`
 * refuses a numeric marker with no lab min and lab max, so a panel that shipped
 * one would land in the library as a row that cannot be saved.
 *
 * Five of the first six Labcorp panels reproduce, marker for marker, sections
 * this app's first practitioner had already built by hand from her own
 * reports. That
 * agreement is why the generated ranges can be trusted, and where she curated
 * something the report does not print — an alarm bound off the lab's
 * interpretive legend — her value is the one here, noted per marker.
 *
 * A PRINTED "<n" IS STORED AS n-1, AND A PRINTED "<=n" AS n. This is the one
 * rule in here that is easy to get wrong in a way nothing catches, because it
 * only misgrades a result landing EXACTLY on the boundary. Quest prints
 * "Optimal <1138; Moderate 1138-1409", so 1138 is the first abnormal value;
 * `isHigh` is `value > labMax`, so a stored 1138 charts a 1138 as normal when
 * the lab calls it moderate. Stored as 1137 it is flagged, which is what the
 * lab means and what the app's own chart-type help tells a practitioner to do
 * ("Lab range '< 36': enter Lab Max 35.9"). The same applies upward: ">6729"
 * is a lab min of 6730, while ">=30.5" is 30.5 because it already includes its
 * boundary.
 *
 * Seven bounds were corrected on 2026-10-11 under this rule (NMR LDL-P and LDL
 * Size, and all five one-sided ion mobility bounds; the two NMR ones left with
 * that panel on 2026-10-22, and the same rule now applies to those markers'
 * BloodworkRange rows instead). Safe to correct precisely because the catalog
 * is a SEED: a library that already took the panel keeps what it took.
 * `everyBoundIsExclusiveSafe` in the test file is what keeps them right from
 * here.
 *
 * EVERY PANEL IS LAB-SCOPED, and that is not caution. Measured across the
 * upload corpus, the same analyte differs by lab in BOTH name and range: LDL
 * particle number is `LDL-P` against `<1000 nmol/L` at Labcorp, `LDL-P` against
 * `<935` at CPL, and `LDL PARTICLE NUMBER` against `732-2035` on Quest's Cardio
 * IQ. A single panel over the three would be wrong twice. Matching is by name
 * (`fillRowsFromImport`), so a panel also only fills from the lab whose
 * spelling it carries.
 */

/** The lab whose catalog a panel comes from. Shown in the picker. */
export enum Lab {
  LABCORP = 'Labcorp',
  QUEST = 'Quest',
}

/** One marker in a panel: a `CustomMarkerDefinition` without an id. */
export type LabPanelMarker = {
  name: string;
  units?: string;
  resultType?: CustomMarkerResultType;
  /** For a marker reported in words: what the lab calls normal. */
  expectedText?: string;
  chartType?: CustomMarkerChartType;
  precision?: number;
  ranges: CustomMarkerRanges;
  /**
   * The lab's Result LOINC. Nothing reads it yet — matching is by name — and it
   * is kept because the direct lab feed will key on it rather than on spelling.
   */
  loinc?: string;
  /**
   * The `Bloodwork` attribute LabSmarts charts this same row as, when it has
   * one. Set ONLY where the catalog and a shipped field name the same marker.
   *
   * Ten markers carry it today, and they are the whole of the overlap: the
   * nine NMR LipoProfile rows and Labcorp's calculated VLDL. Both families are
   * shipped fields held back from prod, which is exactly why their panels are
   * still here (see NMR_LIPOPROFILE_PANEL). The value was taken from the
   * parser's own name table in lambdas/uploadPdf/utils.js, which is the thing
   * that actually decides what a delivered row lands on, rather than from a
   * label: `HDL-P (Total)`, `LP-IR Score` and `VLDL Cholesterol Cal` all
   * resolve there and match no shipped label, so name matching misses three of
   * the ten.
   *
   * Nothing is withdrawn on account of it. The one reader is the delivered
   * leftovers notice, which asks `isShippedMarkerCharted` whether the shipped
   * field is drawn in THIS environment and declines to offer a second copy of
   * a marker the report already charts. In prod, where both families are held,
   * the answer is no and the catalog route is offered exactly as before.
   */
  shippedAs?: string;
};

export type LabPanel = {
  /**
   * Stable forever. Nothing is stored against it today (what a practitioner has
   * already taken is derived by name), so it is here to be referred to rather
   * than persisted.
   */
  id: string;
  lab: Lab;
  /** The test as the lab names it on the order. */
  title: string;
  /**
   * The section name offered when it is added, and offered exactly as written:
   * nothing appends a lab to it. See `panelSectionTitle` for why that changed
   * on 2026-10-13 and what the rule is now. The practitioner can change the
   * whole thing at add time, which is where a lab gets added if they want one.
   *
   * Ignored when `section` is set, because a shipped section already has a
   * name and it is not ours to rename.
   */
  sectionTitle: string;
  /**
   * A SHIPPED report section these markers belong in, rather than a section of
   * their own.
   *
   * Set only where the marker is a row LabSmarts has no field for that prints
   * INSIDE an order whose other rows it already charts: Labcorp's
   * `VLDL Cholesterol Cal` on a lipid panel, the two immature-granulocyte rows
   * on a CBC. Filing those under a section of their own would put one number
   * in a box beside the eight it was printed with, so they go where the
   * practitioner already reads the rest of that order.
   *
   * It is a `Section`, not a name: `CustomMarkerDefinition` already carries
   * both (`section` for a shipped one, `practitionerSection` for a named one)
   * and the My Markers form has offered the shipped sections on its dropdown
   * since custom markers shipped. All this adds is the catalog's ability to
   * pick one. The picker shows it as a fixed line rather than an editable
   * field, because a shipped section is an enum the practitioner cannot rename
   * from here.
   *
   * Must be one of `CUSTOM_MARKER_SECTION_OPTIONS` — a test pins it, because
   * the enum has members (Results Overview, Notes, the Markers Outside Optimal
   * buckets) that are not places a marker can be filed.
   */
  section?: Section;
  /**
   * Every code that returns these results, so a practitioner can check one
   * against what they ordered. Empty for the panels Labcorp's compendium does
   * not carry (its esoteric sends), where membership comes from the reports
   * themselves.
   *
   * Called a test code rather than an order code because that is what the labs
   * call it on the requisition: Labcorp's menu says "Test Number", Quest's says
   * "Test Code", and neither says order. Shown only on the panel a practitioner
   * has opened, never on the rows they are scanning — a number under every
   * title is noise against the one thing that list is for, which is finding the
   * test by name.
   */
  testCodes: string[];
  /** One line under the title in the picker, where the panel needs a caveat. */
  note?: string;
  markers: LabPanelMarker[];
};

const { STANDARD, LOWER_IS_BETTER, HIGHER_IS_BETTER } = CustomMarkerChartType;

/**
 * Quest's Cardio IQ add-ons, defined once and offered twice.
 *
 * These are Cleveland HeartLab assays Quest runs under its Cardio IQ brand,
 * and each is ORDERED SEPARATELY: a practitioner adds Lp-PLA2 to a lipid panel
 * without ordering TMAO, and the reports on disk carry different subsets of
 * them. So each is a panel of its own below, and there is also one bundle for
 * the practitioner who ordered the lot. Taking both costs nothing:
 * `markersFromPanel` is idempotent by name, so the second pick adds nothing
 * the first already wrote.
 *
 * WHERE THE RANGES COME FROM, and the one place Quest contradicts itself.
 * Every number here is the RISK CUT-OFF printed beside the row, which is the
 * same reading the ion mobility panel makes and for the same reason: the
 * cut-off is what the lab flags H against. Lp-PLA2 is the row that needs the
 * ruling, because it prints two different things across the four reports that
 * carry it — `<124 nmol/min/mL` on two, `<=123` on a third, and
 * `50-133 nmol/min/mL` on a fourth. The fourth is the POPULATION range, and
 * the same report spells the cut-off out underneath it ("Optimal <=123; High
 * >123"), so all four agree once the population range is set aside: 123 is the
 * highest activity the lab still calls optimal.
 *
 * A PRINTED "<n" IS STORED AS n-1, which is the app's own rule for a one-sided
 * range (see the chart-type help: 'Lab range "< 36": enter Lab Max 35.9'). So
 * `<100 ng/mL` is a lab max of 99 and `<6.2 uM` is 6.1. Lp-PLA2 is the
 * exception that proves it: Quest prints `<=123`, which is inclusive, so 123
 * is stored as-is.
 */
const CARDIO_IQ: { [key: string]: LabPanelMarker } = {
  adma: {
    name: 'ADMA',
    units: 'ng/mL',
    chartType: LOWER_IS_BETTER,
    precision: 0,
    ranges: { labMin: 0, labMax: 99 },
  },
  sdma: {
    // The one two-sided row: Quest prints a population interval for SDMA
    // rather than a cut-off, because low SDMA is not a finding the lab reads.
    name: 'SDMA',
    units: 'ng/mL',
    chartType: STANDARD,
    precision: 0,
    ranges: { labMin: 73, labMax: 135 },
  },
  lpPla2: {
    name: 'LP PLA2 ACTIVITY',
    units: 'nmol/min/mL',
    chartType: LOWER_IS_BETTER,
    precision: 0,
    ranges: { labMin: 0, labMax: 123 },
  },
  tmao: {
    name: 'TMAO (TRIMETHYLAMINE N OXIDE)',
    units: 'uM',
    chartType: LOWER_IS_BETTER,
    precision: 1,
    ranges: { labMin: 0, labMax: 6.1 },
  },
  mpo: {
    name: 'MYELOPEROXIDASE',
    units: 'pmol/L',
    chartType: LOWER_IS_BETTER,
    precision: 0,
    ranges: { labMin: 0, labMax: 469 },
  },
  oxLdl: {
    name: 'OxLDL',
    units: 'U/L',
    chartType: LOWER_IS_BETTER,
    precision: 0,
    ranges: { labMin: 0, labMax: 59 },
  },
  pcadScore: {
    // No units: a percentile score from 0 to 100.
    name: 'HDLFX PCAD SCORE',
    chartType: LOWER_IS_BETTER,
    precision: 0,
    ranges: { labMin: 0, labMax: 71 },
  },
  pcec: {
    name: 'HDLFX PCEC',
    units: '% efflux/4hr',
    chartType: STANDARD,
    precision: 1,
    ranges: { labMin: 9.1, labMax: 15.6 },
  },
  insulinResistanceScore: {
    /*
     * Three bands rather than two, printed under the row: "Insulin Sensitive
     * <33; Impaired Insulin Sensitivity 33-66; Insulin Resistant >66". Lab max
     * is the top of the sensitive band and alarm max is where the lab calls
     * the patient resistant, which is the shape the ion mobility panel uses.
     *
     * NOT Labcorp's LP-IR Score, although both are called an insulin
     * resistance score and both run 0 to 100. This one is computed from
     * insulin and C-peptide against a cut-off of 66; Labcorp's comes from the
     * NMR lipoprotein parameters against 45.
     */
    name: 'INSULIN RESISTANCE SCORE',
    chartType: LOWER_IS_BETTER,
    precision: 0,
    ranges: { labMin: 0, labMax: 32, alarmMax: 66 },
  },
};

/**
 * The Labcorp urinalysis, in the two halves Labcorp itself orders it in.
 *
 * `Urinalysis, Routine` (003038) is the dipstick. `Urinalysis, Complete`
 * (003772) is that same dipstick PLUS the microscopic, and
 * `UA/M w/rflx Culture` (377036/377200) is Complete plus a reflex culture. So
 * these are not two complementary panels a practitioner picks between; they
 * are a smaller order and a larger one, and the larger contains the smaller.
 * Both are offered, because a practitioner should be able to take the panel
 * whose name is on the requisition in front of them.
 *
 * Defined once and used twice, the same way CARDIO_IQ is: the two panels share
 * these objects rather than copying them, so the dipstick cannot drift between
 * the two and taking both never writes a row twice.
 */
const URINALYSIS_DIPSTICK: LabPanelMarker[] = [
  {
    name: 'Specific Gravity',
    chartType: STANDARD,
    precision: 3,
    ranges: { labMin: 1.005, labMax: 1.03 },
    loinc: '5811-5',
  },
  {
    name: 'Urine pH',
    chartType: STANDARD,
    precision: 1,
    ranges: { labMin: 5, labMax: 7.5 },
    loinc: '5803-2',
  },
  {
    name: 'Urine-Color',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'Yellow',
    ranges: {},
    loinc: '5778-6',
  },
  {
    name: 'Appearance',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'Clear',
    ranges: {},
    loinc: '5767-9',
  },
  {
    name: 'WBC Esterase',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'Negative',
    ranges: {},
    loinc: '5799-2',
  },
  {
    // The reference column reads "Negative/Trace" and the result reads
    // `Negative` on all ten reports. The RESULT is what an expectation is
    // compared against, the same trap the Quest LDL pattern row documents.
    name: 'Urine Protein',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'Negative',
    ranges: {},
    loinc: '20454-5',
  },
  {
    name: 'Urine Glucose',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'Negative',
    ranges: {},
    loinc: '25428-4',
  },
  {
    name: 'Ketones',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'Negative',
    ranges: {},
    loinc: '2514-8',
  },
  {
    name: 'Urine Blood',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'Negative',
    ranges: {},
    loinc: '5794-3',
  },
  {
    name: 'Urine Bilirubin',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'Negative',
    ranges: {},
    loinc: '5770-3',
  },
  {
    name: 'Urobilinogen,Semi-Qn',
    units: 'mg/dL',
    chartType: STANDARD,
    precision: 1,
    ranges: { labMin: 0.2, labMax: 1 },
    loinc: '20405-7',
  },
  {
    name: 'Nitrite, Urine',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'Negative',
    ranges: {},
    loinc: '5802-4',
  },
];

/**
 * The microscopic half, which only `Urinalysis, Complete` carries.
 *
 * FIVE OF THESE SIX SHIP WITHOUT AN EXPECTED RESULT, on purpose.
 * `matchesExpectedText` is an exact match, and these five have TWO normal
 * results rather than one: the counts read `None` on one report and `0-5` on
 * the next, both inside the reference, and bacteria reads `None` or `Few`. An
 * expectation would mark half the normal results as disagreeing with it, which
 * is worse than recording the value and leaving the reading to the
 * practitioner. Mucus threads is only ever printed when there are some.
 */
const URINALYSIS_MICROSCOPIC: LabPanelMarker[] = [
  {
    name: 'Urine WBC per Field',
    resultType: CustomMarkerResultType.TEXT,
    ranges: {},
  },
  {
    name: 'Urine RBC per Field',
    resultType: CustomMarkerResultType.TEXT,
    ranges: {},
  },
  {
    name: 'Epithelial Cells (non renal)',
    resultType: CustomMarkerResultType.TEXT,
    ranges: {},
  },
  {
    // One normal result rather than two, unlike the rows around it:
    // casts read `None` on all ten reports, against a reference of
    // "None seen". The RESULT is what an expectation is matched against.
    name: 'Casts',
    resultType: CustomMarkerResultType.TEXT,
    expectedText: 'None',
    ranges: {},
  },
  {
    name: 'Bacteria',
    resultType: CustomMarkerResultType.TEXT,
    ranges: {},
  },
  {
    name: 'Mucus Threads',
    resultType: CustomMarkerResultType.TEXT,
    ranges: {},
  },
];

/**
 * The NMR LipoProfile panel — the LIVE route to these nine markers, in every
 * environment.
 *
 * The same nine are also shipped `Bloodwork` fields as of 2026-10-22, but those
 * are held back from prod (`nmrLipoProfileVisible`) and are not finished as a
 * feature: no `BloodworkRange` rows exist yet, so they grade against nothing.
 * This panel is what practitioners actually use, and it has been reachable in
 * prod since prebuilt panels shipped on 2026-10-13. It stays until the shipped
 * markers replace it, rather than being withdrawn in advance of them.
 *
 * ⚠ THE ONE COST, and it is deliberate: in any environment where the shipped
 * markers ARE visible (local dev and slava), a practitioner who adds this panel
 * holds nine CUSTOM markers whose names match nine shipped ones, so a test can
 * carry two of each, free to disagree. `no panel marker shares a name with a
 * marker LabSmarts ships` in the test file carries an explicit exemption for
 * exactly these nine and nothing else. Visible and recoverable on a staging
 * environment is the right trade against withdrawing a working panel from the
 * live one.
 *
 * DELETE THIS WHOLE CONST, its LAB_PANELS entry and that test exemption as part
 * of the release that turns `nmrLipoProfileVisible()` to `true`. Read the
 * migration note in nmrLipoProfileVisibility.ts first: a practitioner who
 * already took this panel keeps those custom rows, and they collide with the
 * shipped markers rather than merging.
 */
const NMR_LIPOPROFILE_PANEL: LabPanel = {
  id: 'labcorp-nmr-lipoprofile',
  lab: Lab.LABCORP,
  title: 'NMR LipoProfile',
  sectionTitle: 'NMR LipoProfile',
  /*
   * Labcorp sells this under nine order codes, one per combination of add-ons
   * (lipids, the insulin-resistance markers, the graph). The nine markers here
   * are the union: an order without the IR block simply leaves four of them
   * unfilled, which is what a blank carried-forward row already does.
   *
   * DHA Laboratory resells the same NMR assay and prints these rows with the
   * same names and the same intervals, so this panel would serve a DHA report
   * too. CPL (`LDL-P` against `<935`, `sLDL-P` against `<467`) and Quest's
   * Cardio IQ (`LDL PARTICLE NUMBER`, `732-2035`) do not, and need their own.
   */
  testCodes: [
    '884318',
    '884209',
    '884247',
    '884000',
    '123489',
    '123497',
    '123638',
    '123810',
  ],
  markers: [
    {
      name: 'LDL-P',
      units: 'nmol/L',
      chartType: LOWER_IS_BETTER,
      precision: 0,
      ranges: { labMin: 0, labMax: 999 },
      loinc: '54434-6',
      shippedAs: 'ldlParticleNumber',
    },
    {
      name: 'HDL-P (Total)',
      units: 'umol/L',
      chartType: HIGHER_IS_BETTER,
      precision: 1,
      ranges: { labMin: 30.5 },
      loinc: '49748-7',
      shippedAs: 'hdlParticleNumber',
    },
    {
      // Alarm max 839 is the lab's own 75th percentile, the top of the band
      // it labels "High" in the interpretive table beside this row.
      name: 'Small LDL-P',
      units: 'nmol/L',
      chartType: LOWER_IS_BETTER,
      precision: 0,
      ranges: { labMin: 0, labMax: 527, alarmMax: 839 },
      loinc: '43727-7',
      shippedAs: 'smallLdlParticleNumber',
    },
    {
      /*
       * Labcorp prints LDL Size TWICE on a combined report, under two
       * different cut-offs: >20.5 in the particle-size block and >=20.8 in
       * the insulin-resistance block (result codes 884309 and 884317, same
       * LOINC). A library holds one marker per name, so this is the
       * particle-size one, 20.5 being the Pattern A / Pattern B boundary the
       * report's own legend draws.
       */
      name: 'LDL Size',
      units: 'nm',
      chartType: HIGHER_IS_BETTER,
      precision: 1,
      ranges: { labMin: 20.6 },
      loinc: '17782-4',
      shippedAs: 'ldlSize',
    },
    {
      name: 'Large VLDL-P',
      units: 'nmol/L',
      chartType: LOWER_IS_BETTER,
      precision: 1,
      ranges: { labMin: 0, labMax: 2.7 },
      loinc: '43728-5',
      shippedAs: 'largeVldlParticleNumber',
    },
    {
      name: 'Large HDL-P',
      units: 'umol/L',
      chartType: HIGHER_IS_BETTER,
      precision: 1,
      ranges: { labMin: 4.8 },
      loinc: '43729-3',
      shippedAs: 'largeHdlParticleNumber',
    },
    {
      name: 'VLDL Size',
      units: 'nm',
      chartType: LOWER_IS_BETTER,
      precision: 1,
      ranges: { labMin: 0, labMax: 46.6 },
      loinc: '62254-8',
      shippedAs: 'vldlSize',
    },
    {
      name: 'HDL Size',
      units: 'nm',
      chartType: HIGHER_IS_BETTER,
      precision: 1,
      ranges: { labMin: 9.2 },
      loinc: '62253-0',
      shippedAs: 'hdlSize',
    },
    {
      // No units: an index from 0 to 100.
      name: 'LP-IR Score',
      chartType: LOWER_IS_BETTER,
      precision: 0,
      ranges: { labMin: 0, labMax: 45 },
      loinc: '62255-5',
      shippedAs: 'lpIrScore',
    },
  ]
};

export const LAB_PANELS: LabPanel[] = [
  {
    id: 'labcorp-celiac-disease-comprehensive',
    lab: Lab.LABCORP,
    title: 'Celiac Disease Comprehensive',
    sectionTitle: 'Celiac Disease Comprehensive',
    testCodes: ['165126'],
    markers: [
      /*
       * Alarm max on the four antibodies is the top of the lab's WEAK POSITIVE
       * band, printed as prose under each row ("Negative 0 - 19, Weak Positive
       * 20 - 30, Moderate to Strong Positive >30") rather than in a column.
       * Above it the lab calls the result positive outright, which is what an
       * alarm bound is for. Read by hand; the generator cannot mine prose.
       */
      {
        name: 'Deamidated Gliadin Abs, IgA',
        units: 'units',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 19, optimalMax: 19, alarmMax: 30 },
        loinc: '58709-7',
      },
      {
        name: 'Deamidated Gliadin Abs, IgG',
        units: 'units',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 19, optimalMax: 19, alarmMax: 30 },
        loinc: '58710-5',
      },
      {
        name: 't-Transglutaminase (tTG) IgA',
        units: 'U/mL',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 3, optimalMax: 3, alarmMax: 10 },
        loinc: '31017-7',
      },
      {
        name: 't-Transglutaminase (tTG) IgG',
        units: 'U/mL',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 5, optimalMax: 5, alarmMax: 9 },
        loinc: '32998-7',
      },
      {
        // Reported in words. Labcorp prints "Negative" as both the result and
        // the reference, and a number is never printed for this row.
        name: 'Endomysial Antibody IgA',
        resultType: CustomMarkerResultType.TEXT,
        expectedText: 'Negative',
        ranges: {},
        loinc: '10362-2',
      },
      {
        // Two-sided, and the reason the panel includes it: a low total IgA
        // makes the three IgA antibodies above uninterpretable.
        name: 'Immunoglobulin A, Qn, Serum',
        units: 'mg/dL',
        chartType: STANDARD,
        precision: 0,
        ranges: { labMin: 87, labMax: 352 },
        loinc: '2458-8',
      },
    ],
  },
  {
    id: 'labcorp-cd4-cd8-ratio-profile',
    lab: Lab.LABCORP,
    title: 'CD4/CD8 Ratio Profile',
    sectionTitle: 'CD4/CD8 Ratio Profile',
    testCodes: ['505271'],
    /*
     * The order returns 29 results and 14 of them are markers LabSmarts already
     * charts (the whole CBC with differential), which belong in their own
     * sections and not here. Of the rest, `Immature Granulocytes` and `NRBC`
     * have no reference interval to chart against ("Not Estab."), and
     * `Immature Grans (Abs)` is a CBC extra that happens to print inside this
     * order rather than part of the CD4/CD8 question. These five are the panel.
     */
    note: 'The CBC printed with this order charts in its own sections.',
    markers: [
      {
        name: 'Absolute CD 4 Helper',
        units: '/uL',
        chartType: STANDARD,
        precision: 0,
        ranges: { labMin: 359, labMax: 1519 },
        loinc: '24467-3',
      },
      {
        name: '% CD 4 Pos. Lymph.',
        units: '%',
        chartType: STANDARD,
        precision: 1,
        ranges: { labMin: 30.8, labMax: 58.5 },
        loinc: '8123-2',
      },
      {
        name: 'Abs. CD 8 Suppressor',
        units: '/uL',
        chartType: STANDARD,
        precision: 0,
        ranges: { labMin: 109, labMax: 897 },
        loinc: '14135-8',
      },
      {
        name: '% CD 8 Pos. Lymph.',
        units: '%',
        chartType: STANDARD,
        precision: 1,
        ranges: { labMin: 12, labMax: 35.5 },
        loinc: '8101-8',
      },
      {
        // No units: it is a ratio of the two percentages above.
        name: 'CD4/CD8 Ratio',
        chartType: STANDARD,
        precision: 2,
        ranges: { labMin: 0.92, labMax: 3.72 },
        loinc: '54218-3',
      },
    ],
  },
  {
    id: 'labcorp-mold-igg-allergens',
    lab: Lab.LABCORP,
    title: 'IgG Allergens(14) Molds',
    sectionTitle: 'Molds - IgG Allergens',
    /*
     * Not in the compendium at all, so there is no order code to show and the
     * membership is the fourteen rows the reports print. Labcorp's compendium
     * DOES carry "Allergen Profile, Mold" (062448), and that is a different
     * test: twelve mold IgE allergens in kU/L.
     *
     * The names are Labcorp's own truncations, which is why they read as they
     * do ("M005-IgG Candida albican"). They are what the import offers, so they
     * are what the panel has to be called. The two asterisks Labcorp prints in
     * front of each one are stripped by `withoutLeadingStars` before the row is
     * offered, and travel as that row's alias, so either spelling matches.
     */
    testCodes: [],
    note: 'IgG, not IgE. Labcorp’s "Allergen Profile, Mold" is the IgE test.',
    markers: [
      'M001-IgG Penicillium chrysog',
      'M002-IgG Cladosporium herbar',
      'M003-IgG Aspergillus fumigat',
      'M004-IgG Mucor racemosus',
      'M005-IgG Candida albican',
      'M006-IgG Alternaria alternat',
      'M007-IgG Botrytis cinerea',
      'M008-IgG Setomelanomma rosta',
      'M009-IgG Fusarium proliferat',
      'M010-IgG Stemphylium herbaru',
      'M011-IgG Rhizopus nigricans',
      'M012-IgG Aureobasidi pullula',
      'M014-IgG Epicoccum purpur',
      'M207-IgG Aspergillus niger',
    ].map(name => ({
      name,
      units: 'ug/mL',
      chartType: LOWER_IS_BETTER,
      precision: 1,
      // One interval for all fourteen, measured identical on three reports.
      ranges: { labMin: 0, labMax: 1.9 },
    })),
  },
  {
    id: 'labcorp-omegacheck',
    lab: Lab.LABCORP,
    title: 'OmegaCheck (EPA+DPA+DHA)',
    sectionTitle: 'Fatty Acid Status',
    /*
     * Another esoteric send the compendium does not list; membership is the
     * report's.
     *
     * THE ONE PANEL THAT SERVES BOTH LABS, and the only one that should.
     * Labcorp and Quest both send OmegaCheck to Quest's Cardiometabolic Center
     * of Excellence at Cleveland HeartLab, so it is one assay with one set of
     * reference intervals, and `CustomMarker.keyFor` lowercases — which leaves
     * eight of these nine rows matching a Quest report's spelling as they
     * stand: `ARACHIDONIC ACID/EPA RATIO`, `OMEGA-6/OMEGA-3 RATIO`, `EPA`,
     * `DPA`, `DHA`, `ARACHIDONIC ACID`, `LINOLEIC ACID`, `OMEGA-3 TOTAL`.
     *
     * Only the headline row is genuinely two names, Labcorp's `OmegaCheck(TM)`
     * against Quest's `EPA+DPA+DHA`, and that is settled in the PARSER rather
     * than here: `OFFERED_AS` in lambdas/uploadPdf/parsers/quest.js offers the
     * Quest row under this panel's name and carries the printed spelling as its
     * alias. Shipping a second, Quest-spelled copy of this panel instead would
     * put two headline markers on the same test, one of them always blank.
     *
     * Not the pattern for the lipoprotein panels, which ARE two different
     * tests and ship as two: see quest-lipoprotein-fractionation-ion-mobility
     * below.
     *
     * ITS SECTION WAS THE FIRST TO DROP THE LAB, on 2026-10-13, and a few
     * hours later every other panel followed it (see `panelSectionTitle`).
     * Worth keeping the measurement that justified going first: across all 109
     * corpus reports, OmegaCheck rows are printed by Labcorp
     * (`OmegaCheck(TM) | 9.4 | % by wt | >5.4`), by the Quest-sourced Function
     * report through Health Gorilla (`EPA+DPA+DHA`, `OMEGACHECK(R)`,
     * `ARACHIDONIC ACID/EPA RATIO`) and by the Function Health app export. It
     * is the ONLY panel in this catalog that more than one lab prints, so its
     * section demonstrably holds two labs' results.
     *
     * THE LAB STAYS ON `lab`, which is not a contradiction: the picker still
     * lists this under Labcorp, because that is whose catalog these names and
     * bounds come from, and that is a different claim from what the section is
     * called.
     */
    testCodes: [],
    markers: [
      {
        /*
         * 5.5, not the 5.4 the reference column prints. The row's own comment
         * states the cut-off as ">=5.5% by wt", Quest states 5.5 for the same
         * assay, and 5.5 is what this app's first practitioner curated. The
         * printed 5.4 appears to be the assay's reporting precision rather than
         * the clinical boundary.
         */
        name: 'OmegaCheck(TM)',
        units: '% by wt',
        chartType: HIGHER_IS_BETTER,
        precision: 1,
        ranges: { labMin: 5.5, optimalMin: 5.5 },
      },
      {
        // Labcorp prints no interval for this row. It is the same quantity the
        // OmegaCheck cut-off is set on, so it takes the same bound.
        name: 'Omega-3 total',
        units: '% by wt',
        chartType: HIGHER_IS_BETTER,
        precision: 1,
        ranges: { labMin: 5.5, optimalMin: 5.5 },
      },
      {
        name: 'Arachidonic Acid/EPA Ratio',
        chartType: STANDARD,
        precision: 1,
        ranges: { labMin: 3.7, labMax: 40.7 },
      },
      {
        name: 'Omega-6/Omega-3 Ratio',
        chartType: STANDARD,
        precision: 1,
        ranges: { labMin: 3.7, labMax: 14.4 },
      },
      {
        name: 'EPA',
        units: '% by wt',
        chartType: STANDARD,
        precision: 1,
        ranges: { labMin: 0.2, labMax: 2.3 },
      },
      {
        name: 'DPA',
        units: '% by wt',
        chartType: STANDARD,
        precision: 1,
        ranges: { labMin: 0.8, labMax: 1.8 },
      },
      {
        name: 'DHA',
        units: '% by wt',
        chartType: STANDARD,
        precision: 1,
        ranges: { labMin: 1.4, labMax: 5.1 },
      },
      {
        name: 'Arachidonic Acid',
        units: '% by wt',
        chartType: STANDARD,
        precision: 1,
        ranges: { labMin: 8.6, labMax: 15.6 },
      },
      {
        name: 'Linoleic Acid',
        units: '% by wt',
        chartType: STANDARD,
        precision: 1,
        ranges: { labMin: 18.6, labMax: 29.5 },
      },
      /*
       * `Omega-6 total` is deliberately out. Labcorp prints no interval for it
       * and there is no second source for one, so it could only ship as a row
       * reported in words, which charts nothing. The import still offers it to
       * anyone who wants to define it.
       */
    ],
  },
  {
    id: 'labcorp-ebv-antibody-profile',
    lab: Lab.LABCORP,
    title: 'EBV Antibody Profile',
    sectionTitle: 'EBV Antibody Profile',
    testCodes: ['240610'],
    /*
     * Three results; the order's fourth row is the lab's interpretation
     * paragraph. `EBV Early Antigen Ab, IgG` is NOT in this panel: it is its own
     * order (096248), often run alongside.
     *
     * Alarm max is the top of the lab's EQUIVOCAL band, printed under each row
     * ("Negative <18.0, Equivocal 18.0 - 21.9, Positive >21.9"), the same rule
     * the celiac panel uses.
     */
    markers: [
      {
        name: 'EBV Ab VCA, IgM',
        units: 'U/mL',
        chartType: LOWER_IS_BETTER,
        precision: 1,
        ranges: { labMin: 0, labMax: 35.9, optimalMax: 35.9, alarmMax: 43.9 },
        loinc: '5159-9',
      },
      {
        name: 'EBV Ab VCA, IgG',
        units: 'U/mL',
        chartType: LOWER_IS_BETTER,
        precision: 1,
        ranges: { labMin: 0, labMax: 17.9, optimalMax: 17.9, alarmMax: 21.9 },
        loinc: '5157-3',
      },
      {
        /*
         * THE FOURTH ROW, added 2026-10-13. Labcorp's EBV Antibody Profile
         * prints four antibodies and this catalog offered three until someone
         * asked what else was missing. Found by counting unplaced rows across
         * the corpus: 3 Labcorp reports print it, against a consistent
         * `0.0-8.9 U/mL`.
         *
         * The alarm bound is the practitioner's, like the three rows around
         * it: Labcorp states the equivocal band in prose under the row, which
         * the generator cannot mine, and her library carries 10.9.
         */
        name: 'EBV Early Antigen Ab, IgG',
        units: 'U/mL',
        chartType: CustomMarkerChartType.LOWER_IS_BETTER,
        precision: 1,
        ranges: { labMin: 0, labMax: 8.9, optimalMax: 8.9, alarmMax: 10.9 },
        loinc: '50969-5',
      },
      {
        name: 'EBV Nuclear Antigen Ab, IgG',
        units: 'U/mL',
        chartType: LOWER_IS_BETTER,
        precision: 1,
        ranges: { labMin: 0, labMax: 17.9, optimalMax: 17.9, alarmMax: 21.9 },
        loinc: '30083-0',
      },
    ],
  },
  /*
   * The Quest ion-mobility panel below is a DIFFERENT TEST from the NMR one
   * above, not a second spelling of it: Angstroms rather than nanometres, its
   * own cut-offs, and a literal `LDL PATTERN` letter. None of its names collide
   * with the NMR nine, and none of its numbers may be charted against their
   * ranges.
   */
  NMR_LIPOPROFILE_PANEL,
  {
    id: 'quest-lipoprotein-fractionation-ion-mobility',
    lab: Lab.QUEST,
    title: 'Lipoprotein Fractionation, Ion Mobility',
    sectionTitle: 'Lipoprotein Fractionation',
    /*
     * Quest's answer to the same clinical question as Labcorp's NMR LipoProfile,
     * and a DIFFERENT TEST rather than a second spelling of it. Measured across
     * the Quest-family reports on disk: different method (ion mobility against
     * NMR), different analytes, different units and different cut-offs.
     *
     *   Labcorp NMR                    Quest ion mobility
     *   LDL-P          <1000 nmol/L    LDL PARTICLE NUMBER  <1138 nmol/L
     *   Small LDL-P    <=527 nmol/L    LDL SMALL <142, LDL MEDIUM <215
     *   HDL-P (Total)  >=30.5 umol/L   HDL LARGE            >6729 nmol/L
     *   LDL Size       >20.5 nm        LDL PEAK SIZE  >222.9 Angstrom
     *
     * Note the sizes especially: 222.9 Angstrom IS 22.29 nm, so a practitioner
     * holding one marker for both labs would be charting Angstroms against a
     * range in nanometres. Quest's own insulin resistance score belongs to a
     * different order and is NOT here: it is computed from insulin and
     * C-peptide against a cut-off of 66, where Labcorp's LP-IR Score comes from
     * the NMR parameters against 45.
     *
     * WHERE THE RANGES COME FROM. Quest prints two things per row: a risk
     * cut-off ("Optimal <1138; Moderate 1138-1409; High >1409") and a
     * population reference ("Male and Female Reference Range: 1016 to 2185").
     * The cut-offs are what the row is read against, and they are what the lab
     * flags H and L on, so they are the range here: lab max is the optimal
     * boundary and alarm max is where the lab calls it high. That is the same
     * shape the celiac and EBV panels use for their legends. The population
     * ranges are deliberately NOT used: a marker whose lab max is 2185 would
     * show an LDL particle number of 1400 as unremarkable.
     *
     * No order code: Quest's catalog is not on disk the way Labcorp's
     * compendium is, so membership is the six rows the reports print.
     */
    testCodes: [],
    note: 'Ion mobility. Not interchangeable with Labcorp\u2019s NMR LipoProfile.',
    markers: [
      {
        name: 'LDL PARTICLE NUMBER',
        units: 'nmol/L',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 1137, alarmMax: 1409 },
      },
      {
        name: 'LDL SMALL',
        units: 'nmol/L',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 141, alarmMax: 219 },
      },
      {
        name: 'LDL MEDIUM',
        units: 'nmol/L',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 214, alarmMax: 301 },
      },
      {
        // The one row where high is the good end, so the alarm is a floor:
        // "Optimal >6729; Moderate 6729-5353; High <5353" reads as risk, not as
        // the analyte.
        name: 'HDL LARGE',
        units: 'nmol/L',
        chartType: HIGHER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 6730, alarmMin: 5353 },
      },
      {
        name: 'LDL PEAK SIZE',
        units: 'Angstrom',
        chartType: HIGHER_IS_BETTER,
        precision: 1,
        ranges: { labMin: 223.0, alarmMin: 217.4 },
      },
      {
        /*
         * Reported in words, and the expectation is the RESULT the lab prints
         * rather than the reference beside it.
         *
         * The reference column reads "A Pattern" and the result column reads a
         * bare `A`, which is what the import now hands over — measured on all
         * seven reports that carry this row, with a `B` on the eighth. So
         * `A Pattern` was the wrong string to expect: `matchesExpectedText` is
         * an exact match once lowercased, so every normal result would have
         * been marked as disagreeing with the expectation.
         *
         * Invisible until the Quest parser started offering its leftovers,
         * because nothing could fill the marker before that.
         */
        name: 'LDL PATTERN',
        resultType: CustomMarkerResultType.TEXT,
        expectedText: 'A',
        ranges: {},
      },
    ],
  },
  {
    id: 'labcorp-urinalysis-routine',
    lab: Lab.LABCORP,
    title: 'Urinalysis, Routine',
    sectionTitle: 'Urinalysis',
    testCodes: ['003038'],
    /*
     * The dipstick alone, which is the smaller of Labcorp's two urinalysis
     * orders. Everything the Complete panel's note says about naming applies
     * here unchanged, because these are literally the same marker objects.
     */
    note: 'The dipstick only. Order 003772 adds the microscopic.',
    markers: URINALYSIS_DIPSTICK,
  },
  {
    id: 'labcorp-urinalysis-complete',
    lab: Lab.LABCORP,
    title: 'Urinalysis, Complete',
    sectionTitle: 'Urinalysis',
    testCodes: ['003772'],
    /*
     * The largest panel in this catalog and the one with the most history, so
     * three things are worth stating plainly.
     *
     * 1. LABCORP'S OWN SPELLINGS, EXCEPT FOR SEVEN. `Appearance` and `Casts`
     *    read fine inside a section called `Urinalysis (Labcorp)` and match
     *    the report in the practitioner's hand, so they keep the printed name.
     *    `Glucose`, `Protein`, `pH`, `Bilirubin`, `WBC`, `RBC` and
     *    `Occult Blood` cannot: an import can neither offer those nor let them
     *    reach a serum field. `URINE_OFFERS` in
     *    lambdas/uploadPdf/parsers/labcorp.js renames those seven on the way
     *    out, and these markers carry the renamed spellings because a panel
     *    only fills from what the import actually offers.
     *
     * 2. ONE SET OF URINE MARKERS, SHARED WITH HOSPITAL JOYA. Decided
     *    2026-10-11, and the reason is the client rather than the catalog: a
     *    marker's identity is its NAME (`CustomMarker.keyFor`), which is what
     *    lines a result up with the same result on an earlier test, so two
     *    lab-scoped sets would give a client who changed labs two half-length
     *    histories of their urine protein instead of one. Where the result
     *    came from matters less than being able to read it over time.
     *
     *    So these markers are deliberately NOT lab-scoped and the section is
     *    plain `Urinalysis`. It needed a `sharedAcrossLabs` flag to say so
     *    until 2026-10-13, when no panel's section carried a lab any more and
     *    the flag had nothing left to express. hospitalJoya.js
     *    carries these spellings as English aliases on its Spanish rows, so a
     *    Joya report fills 16 of the 18, measured against every Joya report on
     *    disk. The other two are differences in what the lab MEASURES rather
     *    than in what it calls it, so neither is aliased: Joya splits
     *    epithelial cells by origin into urethral, urothelial and renal, and
     *    it reports urobilinogen in words where Labcorp reports mg/dL, which a
     *    numeric marker cannot take.
     *
     *    Sharing is only honest because the two labs agree here. A urinalysis
     *    is almost entirely words, a dipstick protein reads Negative at either
     *    lab, and the three numeric rows carry intervals (1.005-1.030,
     *    5.0-7.5, 0.2-1.0) that every lab prints. Where two labs genuinely
     *    differ they also print different NAMES, which is why the lipoprotein
     *    panels need no rule of their own: `LDL-P` against `<1000` and
     *    `LDL PARTICLE NUMBER` against `<1138` are already two markers.
     *
     * 3. THE MARKERS ARE SHARED WITH `Urinalysis, Routine`, which is Labcorp's
     *    smaller order and is this panel's dipstick half exactly. See
     *    URINALYSIS_DIPSTICK, which also explains the five rows that ship with
     *    no expected result.
     */
    note: 'Seven rows are renamed so a dipstick cannot be read as blood.',
    markers: [...URINALYSIS_DIPSTICK, ...URINALYSIS_MICROSCOPIC],
  },
  {
    id: 'labcorp-cmv-antibodies',
    lab: Lab.LABCORP,
    title: 'CMV Abs IgG/IgM',
    sectionTitle: 'CMV Antibodies',
    /*
     * TWO CODES, because there is no combined order. The compendium carries
     * `Cytomegalovirus (CMV) Ab, IgG` (006494) and `Cytomegalovirus (CMV) Ab,
     * IgM` (096727) as separate tests, and `CMV Abs IgG/IgM` is how the report
     * heads them once both were ordered. Both are listed, which is what this
     * field means: every code that returns these results.
     */
    testCodes: ['006494', '096727'],
    /*
     * Alarm max is the top of the lab's EQUIVOCAL band, read off the legend
     * printed under each row ("Negative <0.60, Equivocal 0.60 - 0.69, Positive
     * >0.69"); above it Labcorp calls the result positive. The same reading
     * the celiac and EBV panels make, and the same reason: the legend is prose
     * and the generator cannot mine it.
     */
    markers: [
      {
        name: 'Cytomegalovirus (CMV) Ab, IgG',
        units: 'U/mL',
        chartType: LOWER_IS_BETTER,
        precision: 2,
        ranges: { labMin: 0, labMax: 0.59, optimalMax: 0.59, alarmMax: 0.69 },
        loinc: '5124-3',
      },
      {
        name: 'Cytomegalovirus (CMV) Ab, IgM',
        units: 'AU/mL',
        chartType: LOWER_IS_BETTER,
        precision: 1,
        ranges: { labMin: 0, labMax: 29.9, optimalMax: 29.9, alarmMax: 34.9 },
        loinc: '5126-8',
      },
    ],
  },
  {
    id: 'labcorp-ibd-expanded',
    lab: Lab.LABCORP,
    title: 'IBD Expanded Panel',
    sectionTitle: 'IBD Expanded',
    testCodes: ['162045'],
    // Every range here is a printed interval rather than a cut-off, so each
    // one is stored exactly as the report prints it.
    markers: [
      {
        name: 'gASCA',
        units: 'units',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 50 },
        loinc: '47321-5',
      },
      {
        name: 'ACCA',
        units: 'units',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 90 },
        loinc: '53594-8',
      },
      {
        name: 'ALCA',
        units: 'units',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 60 },
        loinc: '53596-3',
      },
      {
        name: 'AMCA',
        units: 'units',
        chartType: LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 100 },
        loinc: '53597-1',
      },
      {
        name: 'Atypical pANCA',
        resultType: CustomMarkerResultType.TEXT,
        expectedText: 'Negative',
        ranges: {},
        loinc: '53029-5',
      },
    ],
  },
  {
    id: 'quest-cardio-iq-advanced',
    lab: Lab.QUEST,
    title: 'Cardio IQ Advanced Cardiometabolic',
    sectionTitle: 'Cardio IQ Advanced',
    testCodes: [],
    // Every Cardio IQ add-on at once, for the practitioner who ordered them
    // together. The seven panels below are the same markers one order at a
    // time; taking both adds nothing twice. See CARDIO_IQ.
    note: 'All seven Cardio IQ add-ons. Each is also offered on its own.',
    markers: [
      CARDIO_IQ.insulinResistanceScore,
      CARDIO_IQ.lpPla2,
      CARDIO_IQ.mpo,
      CARDIO_IQ.oxLdl,
      CARDIO_IQ.adma,
      CARDIO_IQ.sdma,
      CARDIO_IQ.tmao,
      CARDIO_IQ.pcadScore,
      CARDIO_IQ.pcec,
    ],
  },
  {
    id: 'quest-cardio-iq-insulin-resistance',
    lab: Lab.QUEST,
    title: 'Cardio IQ Insulin Resistance Panel with Score',
    sectionTitle: 'Cardio IQ Insulin Resistance',
    testCodes: [],
    // One marker, because LabSmarts already charts the other two the order
    // returns: the score is computed from insulin and C-peptide, and both of
    // those land in their own sections.
    note: 'Insulin and C-peptide from this order chart in their own sections.',
    markers: [CARDIO_IQ.insulinResistanceScore],
  },
  {
    id: 'quest-cardio-iq-lp-pla2',
    lab: Lab.QUEST,
    title: 'Cardio IQ Lp-PLA2 Activity',
    sectionTitle: 'Cardio IQ Lp-PLA2',
    testCodes: [],
    note: 'Activity, not the discontinued Lp-PLA2 Mass test.',
    markers: [CARDIO_IQ.lpPla2],
  },
  {
    id: 'quest-cardio-iq-mpo',
    lab: Lab.QUEST,
    title: 'Cardio IQ Myeloperoxidase (MPO)',
    sectionTitle: 'Cardio IQ MPO',
    testCodes: [],
    markers: [CARDIO_IQ.mpo],
  },
  {
    id: 'quest-cardio-iq-oxldl',
    lab: Lab.QUEST,
    title: 'Cardio IQ OxLDL',
    sectionTitle: 'Cardio IQ OxLDL',
    testCodes: [],
    markers: [CARDIO_IQ.oxLdl],
  },
  {
    id: 'quest-cardio-iq-adma-sdma',
    lab: Lab.QUEST,
    title: 'Cardio IQ ADMA/SDMA',
    sectionTitle: 'Cardio IQ ADMA/SDMA',
    testCodes: [],
    markers: [CARDIO_IQ.adma, CARDIO_IQ.sdma],
  },
  {
    id: 'quest-cardio-iq-tmao',
    lab: Lab.QUEST,
    title: 'Cardio IQ TMAO',
    sectionTitle: 'Cardio IQ TMAO',
    testCodes: [],
    markers: [CARDIO_IQ.tmao],
  },
  {
    id: 'quest-cardio-iq-hdl-function',
    lab: Lab.QUEST,
    title: 'Cardio IQ HDL Function',
    sectionTitle: 'Cardio IQ HDL Function',
    testCodes: [],
    note: 'HDLfx: the cholesterol efflux capacity and its risk score.',
    markers: [CARDIO_IQ.pcadScore, CARDIO_IQ.pcec],
  },
  /*
   * ── THE ROWS THAT PRINT INSIDE AN ORDER LABSMARTS ALREADY CHARTS ──────────
   *
   * The three below are a different shape from everything above them, and the
   * difference is worth stating because it is the reason the button is called
   * "Add a lab panel/marker" rather than "Add a lab panel".
   *
   * Every other entry here is a test whose results have NO home in the app.
   * These are single rows printed beside rows the app charts already: the lipid
   * panel's VLDL, the CBC's two immature-granulocyte lines. Measured across all
   * 58 reports in the upload corpus on 2026-10-13 by counting unplaced rows per
   * (lab, name), they are the commonest markers in the whole corpus that
   * nothing in LabSmarts can hold: `Immature Grans (Abs)` on 45 Labcorp
   * reports, `UIBC` on 46, `VLDL Cholesterol Cal` on 40, Quest's
   * `NON HDL CHOLESTEROL` on 18.
   *
   * SO THEY CARRY `section` AND FILE INTO THE SHIPPED SECTION. A VLDL in a box
   * of its own, beside the Lipid Balance section holding the eight numbers it
   * was printed with, is worse than not offering it.
   *
   * EACH ENTRY HOLDS ONLY THE ROWS LABSMARTS LACKS, which is why a "Lipid
   * Panel" here is one marker rather than five. The other four are shipped
   * fields; adding them as custom markers would be two of every lipid, and the
   * test "no panel marker shares a name with a marker LabSmarts ships" is what
   * stops it. The note on each entry says so on screen, because a practitioner
   * opening "Lipid Panel" and finding one row needs to know that is the answer
   * and not a bug.
   *
   * UIBC IS DELIBERATELY ABSENT despite being the commonest of the four. Its
   * printed interval is demographic: 131-425, 118-369 and 148-395 all appear
   * across the corpus, so any single pair of bounds here would misgrade some
   * clients. A marker whose range depends on who the client is belongs in
   * BloodworkRange with its own rows, not in a seed that copies one range to
   * everyone.
   *
   * LABCORP'S OWN `Non-HDL Cholesterol` (result code 011976, order 343925
   * "LP+Non-HDL Cholesterol") IS ALSO ABSENT, and not by oversight: no report
   * in the corpus carries it, so there is no printed range to ship, and the
   * catalog's rule is that a bound comes off a real report or does not ship.
   * Quest's spelling is the one with a measured interval.
   */
  {
    id: 'labcorp-lipid-panel-vldl',
    lab: Lab.LABCORP,
    title: 'Lipid Panel',
    // Ignored: `section` wins. Kept so the shape matches every other entry.
    sectionTitle: 'Lipid Panel',
    section: Section.LIPIDS,
    testCodes: ['303756', '221010', '235010', '343925'],
    note:
      'Adds the one row LabSmarts has no field for. The rest of the lipid panel already charts in Lipid Balance.',
    markers: [
      {
        name: 'VLDL Cholesterol Cal',
        units: 'mg/dL',
        // Printed `5-40` on all 40 corpus reports that carry it, with no
        // variation, so both bounds are the lab's and neither is one-sided.
        chartType: CustomMarkerChartType.STANDARD,
        precision: 0,
        ranges: { labMin: 5, labMax: 40 },
        loinc: '13458-5',
        shippedAs: 'vLDL',
      },
    ],
  },
  {
    id: 'labcorp-cbc-immature-granulocytes',
    lab: Lab.LABCORP,
    title: 'CBC With Differential/Platelet',
    sectionTitle: 'CBC With Differential/Platelet',
    /*
     * WBC, NOT RBC. It shipped as RBC on 2026-10-13 and was corrected the same
     * day: immature granulocytes are white cells, and the practitioner's own
     * library had both rows filed under the shipped WBC section all along. Her
     * library is the better authority than the instruction, and the two
     * disagreed.
     */
    section: Section.WBC,
    testCodes: ['005009', '005013', '004800'],
    note:
      'Adds the two immature-granulocyte rows only. The rest of the CBC already charts in its own sections.',
    markers: [
      {
        name: 'Immature Grans (Abs)',
        units: 'x10E3/uL',
        // Printed `0.0-0.1` on all 45 corpus reports, no variation.
        chartType: CustomMarkerChartType.LOWER_IS_BETTER,
        precision: 1,
        ranges: { labMin: 0, labMax: 0.1 },
        loinc: '53115-2',
      },
      {
        name: 'Immature Granulocytes',
        units: '%',
        /*
         * THE ONE RANGE IN THIS CATALOG THE LAB DOES NOT PRINT. Labcorp prints
         * `Not Estab.` beside this row on all 45 reports that carry it, so
         * there is no interval to mine and the generator left the row out.
         *
         * 0 to 0.01 is the user's authored range, 2026-10-13, and it is the
         * same kind of judgement as the celiac and EBV alarm bounds: a number
         * the practitioner supplies where the lab supplies prose. It reads as
         * "any immature granulocytes at all is a finding", which is what the
         * percent row is for.
         *
         * PRECISION 2 BECAUSE OF THAT BOUND, not because the lab reports two
         * decimals: Labcorp prints this row as a whole number (`0` on every
         * corpus report), and a precision of 0 or 1 could not express 0.01 at
         * all. The cost is that a reported 0 displays as 0.00.
         */
        chartType: CustomMarkerChartType.LOWER_IS_BETTER,
        precision: 2,
        ranges: { labMin: 0, labMax: 0.01 },
        loinc: '71695-1',
      },
    ],
  },
  {
    id: 'quest-lipid-panel-non-hdl',
    lab: Lab.QUEST,
    title: 'Lipid Panel',
    sectionTitle: 'Lipid Panel',
    section: Section.LIPIDS,
    // Quest's compendium is not on disk; every Quest entry here leaves its
    // codes empty for the same reason.
    testCodes: [],
    note:
      'Adds the one row LabSmarts has no field for. The rest of the lipid panel already charts in Lipid Balance.',
    markers: [
      {
        name: 'NON HDL CHOLESTEROL',
        units: 'mg/dL',
        /*
         * Quest prints `<130 mg/dL (calc)`, a cut-off rather than a population
         * interval, so 130 is the first abnormal value and the stored bound is
         * 129 at this marker's 0 dp. The printed string is pinned in the test's
         * PRINTED_CUT_OFFS table with the other 21.
         */
        chartType: CustomMarkerChartType.LOWER_IS_BETTER,
        precision: 0,
        ranges: { labMin: 0, labMax: 129 },
      },
    ],
  },
  /*
   * ── SINGLE MARKERS THE CORPUS SAYS PRACTITIONERS ORDER ───────────────────
   *
   * Added 2026-10-13, after a sweep of every unplaced row in all 109 corpus
   * reports: count the rows no shipped marker and no panel above holds, rank
   * them, and these are what is left. The sweep is worth repeating rather than
   * trusting this list, and the method matters more than the result: the 22
   * panels above had already swept the corpus so clean that `UIBC` was the ONLY
   * remaining candidate on four or more reports.
   *
   * THEIR RANGES COME PARTLY FROM THE PRACTITIONER'S OWN LIBRARY, which is a
   * widening of this catalog's sourcing rule and is deliberate. The header says
   * a bound comes off a real report; for `UIBC` the reports disagree with each
   * other, and her library is where the disagreement was already resolved. It
   * is the same authority the OmegaCheck cut-off and every alarm bound above
   * already rest on, and it is stated per marker so no bound here is anonymous.
   */
  {
    id: 'labcorp-iron-and-tibc',
    lab: Lab.LABCORP,
    title: 'Iron and TIBC',
    sectionTitle: 'Iron and TIBC',
    section: Section.IRON,
    testCodes: ['001321', '030577', '042077'],
    note:
      'Adds UIBC only. Iron, TIBC and saturation already chart in Iron Status & Utilization.',
    markers: [
      {
        /*
         * THE MOST-ORDERED MARKER LABSMARTS HAS NO FIELD FOR: 46 of the 58
         * Labcorp reports in the corpus print it, more than any other unplaced
         * row at any lab.
         *
         * IT WAS EXCLUDED ON 2026-10-13 AND ADDED THE SAME DAY. The reason for
         * excluding it was real: the printed interval is not one interval.
         * Across the corpus Labcorp prints `131-425` (four reports), `118-369`
         * and `148-395`, and Access prints `110-370` and `118-369` — so UIBC is
         * demographic, and any single pair of bounds here is wrong for someone.
         *
         * 131-425 IS THE PRACTITIONER'S OWN CHOICE, carried in her library
         * against this spelling, and it is also the interval on four of the six
         * Labcorp reports that print one. That makes it a curated bound rather
         * than a measured one, which is the honest description, and it is why
         * this marker is here rather than in BloodworkRange: a range that
         * varies with who the client is wants demographic ROWS, and a seed
         * copies one range to everyone. If UIBC ever becomes a shipped marker,
         * it should be shipped that way and this entry withdrawn.
         */
        name: 'UIBC',
        units: 'ug/dL',
        chartType: CustomMarkerChartType.STANDARD,
        precision: 0,
        ranges: { labMin: 131, labMax: 425 },
        loinc: '2501-5',
      },
    ],
  },
  {
    id: 'labcorp-soluble-transferrin-receptor',
    lab: Lab.LABCORP,
    title: 'Soluble Transferrin Receptor',
    sectionTitle: 'Soluble Transferrin Receptor',
    section: Section.IRON,
    testCodes: ['143305'],
    markers: [
      {
        /*
         * Iron status that ferritin cannot give on its own: sTfR rises with
         * tissue iron demand and is not an acute-phase reactant, so it reads
         * through the inflammation that lifts ferritin. Two corpus reports,
         * both printing `12.2-27.3 nmol/L`, so this bound is the lab's.
         */
        name: 'Soluble Transferrin Receptor',
        units: 'nmol/L',
        chartType: CustomMarkerChartType.STANDARD,
        precision: 1,
        ranges: { labMin: 12.2, labMax: 27.3 },
        loinc: '33210-6',
      },
    ],
  },
  {
    id: 'quest-methylmalonic-acid',
    lab: Lab.QUEST,
    title: 'Methylmalonic Acid',
    sectionTitle: 'Methylmalonic Acid',
    // Nutrients, where vitaminB12 files: MMA is what a B12 result means at the
    // cell, so it belongs beside B12 rather than in a box of its own.
    section: Section.NUTRIENTS,
    testCodes: [],
    note: 'Functional B12 status. Rises before serum B12 falls.',
    markers: [
      {
        name: 'METHYLMALONIC ACID',
        units: 'nmol/L',
        chartType: CustomMarkerChartType.STANDARD,
        precision: 0,
        // Printed `55-335 nmol/L`. Quest's own interval, both bounds.
        ranges: { labMin: 55, labMax: 335 },
      },
    ],
  },
  {
    id: 'quest-lead-venous',
    lab: Lab.QUEST,
    title: 'Lead (Venous)',
    sectionTitle: 'Heavy Metals',
    testCodes: [],
    markers: [
      {
        name: 'LEAD (VENOUS)',
        units: 'mcg/dL',
        chartType: CustomMarkerChartType.LOWER_IS_BETTER,
        precision: 1,
        // Printed `<3.5 mcg/dL`, so 3.5 is the first abnormal value.
        ranges: { labMin: 0, labMax: 3.4 },
      },
    ],
  },
  {
    id: 'quest-mercury-blood',
    lab: Lab.QUEST,
    title: 'Mercury, Blood',
    // The same section as lead, which is why they are two entries rather than
    // one: they are two orders, and the section is what gathers them. Same
    // shape as the two urinalysis orders filing into one Urinalysis.
    sectionTitle: 'Heavy Metals',
    testCodes: [],
    markers: [
      {
        name: 'MERCURY, BLOOD',
        units: 'mcg/L',
        chartType: CustomMarkerChartType.LOWER_IS_BETTER,
        precision: 0,
        // Printed `<11 mcg/L`.
        ranges: { labMin: 0, labMax: 10 },
      },
    ],
  },
  {
    id: 'quest-rheumatoid-factor',
    lab: Lab.QUEST,
    title: 'Rheumatoid Factor',
    sectionTitle: 'Autoimmune Markers',
    testCodes: [],
    markers: [
      {
        name: 'RHEUMATOID FACTOR',
        units: 'IU/mL',
        chartType: CustomMarkerChartType.LOWER_IS_BETTER,
        precision: 0,
        // Printed `<14 IU/mL`.
        ranges: { labMin: 0, labMax: 13 },
      },
    ],
  },
  {
    id: 'quest-ana-screen',
    lab: Lab.QUEST,
    title: 'ANA Screen, IFA, with reflex to Titer and Pattern',
    // Joins the Rheumatoid Factor section above, under the name this app's
    // first practitioner already uses for it.
    sectionTitle: 'Autoimmune Markers',
    testCodes: [],
    note: 'Three rows: the screen, plus the titer and pattern it reflexes to.',
    markers: [
      {
        /*
         * All three are reported in WORDS, so none carries a range: the screen
         * is POSITIVE or NEGATIVE, the titer is a dilution (`1:80`), and the
         * pattern is a letter or a name.
         */
        name: 'ANA SCREEN, IFA',
        resultType: CustomMarkerResultType.TEXT,
        expectedText: 'NEGATIVE',
        ranges: {},
      },
      {
        /*
         * NO `expectedText` on either of these two, deliberately, the same
         * call the urinalysis makes for its five two-answer rows:
         * `matchesExpectedText` is exact, and a reflex row that only prints
         * when the screen is positive has no single normal result to state.
         */
        name: 'ANA TITER',
        resultType: CustomMarkerResultType.TEXT,
        ranges: {},
      },
      {
        name: 'ANA PATTERN',
        resultType: CustomMarkerResultType.TEXT,
        ranges: {},
      },
    ],
  },
];

/** Panels grouped by lab, in catalog order, for the picker's headings. */
export function panelsByLab(
  panels: LabPanel[] = LAB_PANELS,
): { lab: Lab; panels: LabPanel[] }[] {
  const groups: { lab: Lab; panels: LabPanel[] }[] = [];
  panels.forEach(panel => {
    const group = groups.find(entry => entry.lab === panel.lab);
    if (group) {
      group.panels.push(panel);
    } else {
      groups.push({ lab: panel.lab, panels: [panel] });
    }
  });
  return groups;
}

export function labPanelById(id: string): LabPanel | undefined {
  return LAB_PANELS.find(panel => panel.id === id);
}

/**
 * What a shipped section is called on the My Markers dropdown.
 *
 * Read off `CUSTOM_MARKER_SECTION_OPTIONS` rather than the `Section` enum, so
 * the picker, the dropdown and Report Settings' "Include in PDF" list all say
 * the same words: the enum value for lipids is "Lipids" and the practitioner
 * has learned it as "Lipid Balance". Falls back to the enum value, which is
 * also the report's own heading, for a section the dropdown does not offer.
 */
export function shippedSectionLabel(section: Section): string {
  const option = CUSTOM_MARKER_SECTION_OPTIONS.find(
    candidate => candidate.value === section,
  );
  return option ? option.label : String(section);
}

/**
 * The section name a panel offers.
 *
 * NO LAB IS APPENDED, and this function used to append one to every panel that
 * was not flagged shared. It stopped on 2026-10-13, after the third time the
 * suffix was questioned and the first time it was measured against a real
 * library.
 *
 * WHAT THE MEASUREMENT SHOWED. Checking every offered section name against the
 * first practitioner's 15 sections, FOUR of them were near-duplicates created
 * by the suffix alone: "Celiac Disease Comprehensive (Labcorp)" beside her
 * "Celiac Disease Comprehensive", and the same for "CD4/CD8 Ratio Profile",
 * "Molds - IgG Allergens" and "Autoimmune Markers". The only panel that joined
 * a section she already had was the one whose suffix had just been removed.
 * So the suffix was not labelling sections, it was splitting them, and it
 * defeated the picker's own datalist, which exists to let a panel join a
 * section the practitioner already built.
 *
 * HER CONVENTION, which is the better rule and is now the rule here: a lab
 * belongs in a section name only where you hold two labs' versions of the same
 * panel. Three of her fifteen sections carry one, all Hospital Joya, and the
 * other twelve do not. That is a judgement about what is in a particular
 * library, which the practitioner can make and this catalog cannot, and the
 * section field is editable at add time so they can make it.
 *
 * THE OLD ARGUMENT, for the record: the lab in a section name was "a promise
 * that the ranges in it are that lab's". It was not wrong, it was aimed at the
 * wrong reader. The practitioner already sees the lab on the picker row, on
 * the group bar above it and on the panel they have opened; the person who
 * reads the section name afterwards is the CLIENT, on their report, to whom
 * "(Quest)" is noise.
 */
export function panelSectionTitle(panel: LabPanel): string {
  // A shipped section is named by the app, in the words the report prints and
  // the My Markers dropdown offers. Nothing here renames it.
  if (panel.section) {
    return shippedSectionLabel(panel.section);
  }
  return (panel.sectionTitle || panel.title || '').trim();
}

/**
 * Where a panel's markers are filed: what the practitioner typed, or the
 * panel's own name when they cleared the field.
 *
 * A cleared field falls back rather than filing the markers nowhere. The
 * no-section destination is the appendix, which is every loose marker at once
 * and is deliberately not offered as a panel anywhere else in this feature, so
 * landing fourteen markers there is far more likely to be an empty box than a
 * decision. Shared with the caller, which needs the same answer to decide
 * whether the section has to be created.
 */
export function panelSectionName(panel: LabPanel, sectionName?: string): string {
  return (sectionName || '').trim() || panelSectionTitle(panel);
}

/**
 * The library rows a panel adds, and the markers it leaves alone.
 *
 * Idempotent by NAME, keyed the way `fillRowsFromImport` matches and the way
 * the library's own duplicate check reads (`CustomMarker.keyFor`): a marker
 * already in the library is counted and skipped, never added twice. Two reasons
 * it has to be, and the second is the sharper one:
 *
 *   1. The same panel is often half-typed already, so adding it should fill the
 *      gaps rather than refuse.
 *   2. A name is unique across the WHOLE library, not per section, so a second
 *      copy under a different section is not a duplicate the practitioner can
 *      keep — Save refuses it with "This name is already in My Markers".
 *
 * `takenNames` is every name on the page rather than every name in the saved
 * profile, so a panel added twice before Save still adds nothing twice.
 */
export function markersFromPanel(
  panel: LabPanel,
  sectionName: string,
  takenNames: string[],
  newId: () => string,
): { added: CustomMarkerDefinition[]; alreadyHad: string[] } {
  const taken = new Set<string>();
  (takenNames || []).forEach(name => {
    const key = CustomMarker.keyFor(name || '');
    if (key) {
      taken.add(key);
    }
  });

  const added: CustomMarkerDefinition[] = [];
  const alreadyHad: string[] = [];
  const section = panelSectionName(panel, sectionName);

  (panel.markers || []).forEach(marker => {
    const key = CustomMarker.keyFor(marker.name);
    if (taken.has(key)) {
      alreadyHad.push(marker.name);
      return;
    }
    // Guards a panel that ever holds the same name twice, which would otherwise
    // be two rows Save refuses.
    taken.add(key);
    added.push(
      new CustomMarkerDefinition({
        id: newId(),
        name: marker.name,
        units: marker.units,
        resultType: marker.resultType,
        expectedText: marker.expectedText,
        chartType: marker.chartType || CustomMarkerChartType.STANDARD,
        precision: marker.precision,
        /*
         * One or the other, never both. `CustomMarkerDefinition` treats
         * `section` as the winner (see `assignableSection`), so passing both
         * would leave a `practitionerSection` on the row that nothing reads
         * and that a later edit could resurrect.
         *
         * A shipped section for a row that prints inside an order LabSmarts
         * already charts; otherwise a practitioner-named one, which for every
         * other panel in here is the whole point: those markers are the reason
         * the section exists.
         */
        section: panel.section,
        practitionerSection: panel.section ? undefined : section || undefined,
        ranges: { ...marker.ranges },
      }),
    );
  });

  return { added, alreadyHad };
}
